import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../components/global/layout';
import ApiClient from '../methods/api/apiClient';
import { useDispatch, useSelector } from 'react-redux';
import { login_success } from './actions/user';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import FormControl from "../components/common/FormControl";
import loader from '../methods/loader';
import environment from '../environment';
import { useSearchParams } from 'react-router-dom';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const SizeStyle = Quill.import('attributors/style/size');
SizeStyle.whitelist = ['10px', false, '18px', '32px'];
Quill.register(SizeStyle, true);
const FontStyle = Quill.import('attributors/style/font');
Quill.register(FontStyle, true);

// cw: Draggable logo component - extracted outside Setting to avoid hooks-in-conditional-render error
const DraggableLogo = ({ logoUrl, settings, onUpdateSetting }) => {
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const container = containerRef.current?.parentElement;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    let newX = ((e.clientX - rect.left - dragOffset.current.x) / rect.width) * 100;
    let newY = ((e.clientY - rect.top - dragOffset.current.y) / rect.height) * 100;
    newX = Math.max(0, Math.min(newX, 100 - (settings.size / rect.width) * 100));
    newY = Math.max(0, Math.min(newY, 100));
    onUpdateSetting('x', Math.round(newX * 10) / 10);
    onUpdateSetting('y', Math.round(newY * 10) / 10);
  }, [isDragging, settings.size, onUpdateSetting]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (!logoUrl) return null;

  const handleMouseDown = (e) => {
    e.preventDefault();
    const container = containerRef.current?.parentElement;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const logoX = (settings.x / 100) * rect.width;
    const logoY = (settings.y / 100) * rect.height;
    dragOffset.current = {
      x: e.clientX - rect.left - logoX,
      y: e.clientY - rect.top - logoY
    };
    setIsDragging(true);
  };

  return (
    <>
      <div
        ref={containerRef}
        onMouseDown={handleMouseDown}
        style={{
          position: 'absolute',
          left: `${settings.x}%`,
          top: `${settings.y}%`,
          cursor: isDragging ? 'grabbing' : 'grab',
          zIndex: 10,
          userSelect: 'none',
        }}
      >
        <img
          src={logoUrl}
          alt="Company Logo"
          style={{ width: `${settings.size}px`, height: 'auto', display: 'block', pointerEvents: 'none' }}
          draggable={false}
        />
      </div>
      {settings.padding > 0 && (
        <div style={{ height: `${settings.padding}px`, width: '100%' }} />
      )}
    </>
  );
};

// cw: Draggable text block component for positioning content inside background image
const DraggableTextBlock = ({ children, x, y, onUpdatePosition }) => {
  const blockRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const container = blockRef.current?.parentElement;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    let newX = ((e.clientX - rect.left - dragOffset.current.x) / rect.width) * 100;
    let newY = ((e.clientY - rect.top - dragOffset.current.y) / rect.height) * 100;
    newX = Math.max(0, Math.min(newX, 90));
    newY = Math.max(0, Math.min(newY, 90));
    onUpdatePosition(Math.round(newX * 10) / 10, Math.round(newY * 10) / 10);
  }, [isDragging, onUpdatePosition]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleMouseDown = (e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
    e.preventDefault();
    const container = blockRef.current?.parentElement;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const blockX = (x / 100) * rect.width;
    const blockY = (y / 100) * rect.height;
    dragOffset.current = {
      x: e.clientX - rect.left - blockX,
      y: e.clientY - rect.top - blockY
    };
    setIsDragging(true);
  };

  return (
    <div
      ref={blockRef}
      onMouseDown={handleMouseDown}
      style={{
        position: 'absolute',
        left: `${x}%`,
        top: `${y}%`,
        width: '90%',
        cursor: isDragging ? 'grabbing' : 'grab',
        zIndex: 5,
        userSelect: 'none',
      }}
    >
      {children}
    </div>
  );
};

const Setting = () => {
  const options = [
    { id: 1, name: ' 1 Month' },
    { id: 2, name: '2 Month' },
    { id: 3, name: '3 Month' },
    { id: 4, name: '4 Month' },
    { id: 5, name: '5 Month' },
    { id: 6, name: '6 Month' },
  ];
  const oddHandlingOptions = [
    { id: 'allow_small', name: 'Allow more groups, but some will be fewer than [group size]' },
    { id: 'redistribute', name: 'Allow less groups, but some will be larger than [group size]' }
  ];
  const dispatch = useDispatch()
  // MUST USE REDUX FOR ALL PAGES
  const [form, setForm] = useState(null);
  const [memberGroup, setMemberGroup] = useState(null)
  const [oddHandling, setOddHandling] = useState(null)
  const [getdata, setGetData] = useState()
  const [isLoading, setIsLoading] = useState(true);
  const user = useSelector((state) => state?.user);
  const [connectMeetLabel, setConnectMeetLabel] = useState(user?.connectMeetLabel || 'ConnectMeet');
  const [testimonialLabel, setTestimonialLabel] = useState(user?.testimonialLabel || 'Thank Yous');
  const [hubThreshold, setHubThreshold] = useState(user?.hubThreshold || 5);
  const [superhubThreshold, setSuperhubThreshold] = useState(user?.superhubThreshold || 10);

  // cw: Email header/footer custom text
  const [emailHeaderText, setEmailHeaderText] = useState(null);
  const [emailFooterText, setEmailFooterText] = useState(null);
  const [reminderEmailHeaderText, setReminderEmailHeaderText] = useState(null);
  const [reminderEmailFooterText, setReminderEmailFooterText] = useState(null);
  const [reminderDaysText, setReminderDaysText] = useState('7, 1');
  const [invitationEmailHeaderText, setInvitationEmailHeaderText] = useState(null);
  const [invitationEmailFooterText, setInvitationEmailFooterText] = useState(null);
  const [rsvpEmailHeaderText, setRsvpEmailHeaderText] = useState(null);
  const [rsvpEmailFooterText, setRsvpEmailFooterText] = useState(null);
  const [rsvpHideDateTime, setRsvpHideDateTime] = useState(false);
  const [rsvpHideLocation, setRsvpHideLocation] = useState(false);
  const [allowGuestToGuestThankYou, setAllowGuestToGuestThankYou] = useState(false);
  // toggle between email type customization tabs
  const [emailEditMode, setEmailEditMode] = useState('connectmeet');

  // cw: Company logo state
  const [companyLogoUrl, setCompanyLogoUrl] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef();

  // Per-email logo position (x, y as %) and size (width in px)
  // x/y = position %, size = width px, padding = bottom margin px below logo
  const [logoSettings, setLogoSettings] = useState({
    connectmeet: { x: 0, y: 0, size: 150, padding: 20 },
    reminder: { x: 0, y: 0, size: 150, padding: 20 },
    invitation: { x: 0, y: 0, size: 150, padding: 20 },
    rsvp: { x: 0, y: 0, size: 150, padding: 20 },
  });

  // cw: Zoom integration
  const [searchParams, setSearchParams] = useSearchParams();
  const [zoomStatus, setZoomStatus] = useState({ connected: false, zoomEmail: null });
  const [zoomLoading, setZoomLoading] = useState(false);

  const hasQuillContent = (html) => html && html.replace(/<[^>]*>/g, '').trim().length > 0;

  const quillFormats = [
    'header', 'font', 'size', 'bold', 'italic', 'underline', 'strike',
    'blockquote', 'list', 'bullet', 'indent', 'color', 'align',
  ];
  const quillModules = React.useMemo(() => ({
    toolbar: [
      [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
      [{ size: ['10px', false, '18px', '32px'] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['color'],
      ['clean']
    ],
    clipboard: { matchVisual: false }
  }), []);

  const getZoomStatus = () => {
    const groupId = user?.groupId?._id;
    if (!groupId) return;
    ApiClient.get('api/zoom/status', { groupId }).then(res => {
      if (res.success && res.data) {
        setZoomStatus(res.data);
      }
    });
  };

  useEffect(() => {
    const zoomParam = searchParams.get('zoom');
    if (zoomParam === 'success') {
      toast.success('Zoom connected successfully');
      searchParams.delete('zoom');
      setSearchParams(searchParams, { replace: true });
      getZoomStatus();
    } else if (zoomParam === 'error') {
      toast.error('Failed to connect Zoom. Please try again.');
      searchParams.delete('zoom');
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  useEffect(() => {
    getZoomStatus();
  }, [user?.groupId]);

  const connectZoom = () => {
    const groupId = user?.groupId?._id;
    if (!groupId) return;
    setZoomLoading(true);
    ApiClient.get('api/zoom/auth-url', { groupId }).then(res => {
      setZoomLoading(false);
      if (res.success && res.url) {
        window.location.href = res.url;
      } else {
        toast.error('Failed to get Zoom authorization URL');
      }
    });
  };

  const disconnectZoom = () => {
    const groupId = user?.groupId?._id;
    if (!groupId) return;
    Swal.fire({
      title: 'Disconnect Zoom?',
      text: 'This will remove the Zoom connection for your group.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, disconnect'
    }).then((result) => {
      if (result.isConfirmed) {
        loader(true);
        ApiClient.put('api/zoom/disconnect', { groupId }).then(res => {
          loader(false);
          if (res.success) {
            setZoomStatus({ connected: false, zoomEmail: null });
            toast.success('Zoom disconnected');
          }
        });
      }
    });
  };

  // cw: Background template image
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const [backgroundImageSize, setBackgroundImageSize] = useState(100);
  const [textBlockX, setTextBlockX] = useState(5);
  const [textBlockY, setTextBlockY] = useState(10);
  const [textBlockWidth, setTextBlockWidth] = useState(85);
  const [bgUploading, setBgUploading] = useState(false);
  const bgInputRef = useRef();

  // cw: Group leader's name and group name for email previews
  const groupLeaderName = user?.fullName || 'Group Leader Name';
  const groupName = user?.groupId?.name || 'Sample Group';

  // Collapsed textareas

  const getSettingData = () => {
    const id = user?.groupId?._id;
    loader(true)
    setIsLoading(true);
    ApiClient.get(`api/event-group/setting?groupId=${id}&_t=${Date.now()}`).then(res => {
      if (res.success) {
        loader(false)
        setGetData(res.data);
        if (res?.data && res?.data?.length > 0) {
          const d = res.data[0];
          setMemberGroup(d?.groupMemberLimit);
          setForm(d.historyMonths);
          setOddHandling(d?.oddNumberHandling || 'allow_small');
          setEmailHeaderText(d?.emailHeaderText || '');
          setEmailFooterText(d?.emailFooterText || '');
          setReminderEmailHeaderText(d?.reminderEmailHeaderText || '');
          setReminderEmailFooterText(d?.reminderEmailFooterText || '');
          setReminderDaysText(Array.isArray(d?.reminderDays) && d.reminderDays.length ? d.reminderDays.join(', ') : '7, 1');
          setInvitationEmailHeaderText(d?.invitationEmailHeaderText || '');
          setInvitationEmailFooterText(d?.invitationEmailFooterText || '');
          setRsvpEmailHeaderText(d?.rsvpEmailHeaderText || '');
          setRsvpEmailFooterText(d?.rsvpEmailFooterText || '');
          setRsvpHideDateTime(d?.rsvpHideDateTime || false);
          setRsvpHideLocation(d?.rsvpHideLocation || false);
          setAllowGuestToGuestThankYou(d?.allowGuestToGuestThankYou || false);
          setCompanyLogoUrl(d?.companyLogoUrl || '');
          setBackgroundImageUrl(d?.backgroundImageUrl || '');
          setBackgroundImageSize(d?.backgroundImageSize || 100);
          setTextBlockX(d?.textBlockX || 5);
          setTextBlockY(d?.textBlockY || 10);
          setTextBlockWidth(d?.textBlockWidth || 85);
          setLogoSettings({
            connectmeet: { x: d?.connectmeetLogoX || 0, y: d?.connectmeetLogoY || 0, size: d?.connectmeetLogoSize || 150, padding: d?.connectmeetLogoPadding || 0 },
            reminder: { x: d?.reminderLogoX || 0, y: d?.reminderLogoY || 0, size: d?.reminderLogoSize || 150, padding: d?.reminderLogoPadding || 0 },
            invitation: { x: d?.invitationLogoX || 0, y: d?.invitationLogoY || 0, size: d?.invitationLogoSize || 150, padding: d?.invitationLogoPadding || 0 },
            rsvp: { x: d?.rsvpLogoX || 0, y: d?.rsvpLogoY || 0, size: d?.rsvpLogoSize || 150, padding: d?.rsvpLogoPadding || 0 },
          });
        } else {
          setMemberGroup(3);
          setForm(6);
          setOddHandling('allow_small');
          setEmailHeaderText('');
          setEmailFooterText('');
          setReminderEmailHeaderText('');
          setReminderEmailFooterText('');
          setReminderDaysText('7, 1');
          setInvitationEmailHeaderText('');
          setInvitationEmailFooterText('');
          setRsvpEmailHeaderText('');
          setRsvpEmailFooterText('');
          setCompanyLogoUrl('');
          setBackgroundImageUrl('');
          setBackgroundImageSize(100);
          setTextBlockX(5);
          setTextBlockY(10);
          setTextBlockWidth(85);
        }
      }
      setIsLoading(false);
    });
  };

  const [isDirty, setIsDirty] = useState(false);
  const isDirtyRef = useRef(false);
  const isNavigatingRef = useRef(false);
  const handleSaveRef = useRef(null);
  const handleSaveLabelRef = useRef(null);

  // Keep refs in sync
  useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);

  useEffect(() => {
    getGroupData();
    getSettingData();
  }, []);

  // cw: Mark form as dirty when any setting changes (skip initial load)
  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (!initialLoadDone.current) return;
    setIsDirty(true);
  }, [form, memberGroup, oddHandling, emailHeaderText, emailFooterText,
    reminderEmailHeaderText, reminderEmailFooterText, invitationEmailHeaderText,
    invitationEmailFooterText, rsvpEmailHeaderText, rsvpEmailFooterText,
    rsvpHideDateTime, rsvpHideLocation, allowGuestToGuestThankYou,
    companyLogoUrl, logoSettings, backgroundImageUrl, backgroundImageSize,
    textBlockX, textBlockY, textBlockWidth, connectMeetLabel, testimonialLabel,
    hubThreshold, superhubThreshold]);

  // Mark initial load as done after settings are loaded
  useEffect(() => {
    if (!isLoading) {
      const t = setTimeout(() => { initialLoadDone.current = true; }, 500);
      return () => clearTimeout(t);
    }
  }, [isLoading]);

  // cw: Warn on browser tab close/refresh with unsaved changes (uses ref for sync check)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirtyRef.current && !isNavigatingRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // cw: Intercept link clicks when form is dirty
  useEffect(() => {
    const handleClick = (e) => {
      if (!isDirtyRef.current) return;
      const link = e.target.closest('a[href]');
      if (!link) return;
      const href = link.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('#')) return;
      e.preventDefault();
      e.stopPropagation();
      Swal.fire({
        title: 'Unsaved Changes',
        text: 'You have unsaved changes. Would you like to save before leaving?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Save',
        cancelButtonText: 'Discard',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
      }).then((result) => {
        if (result.isConfirmed) {
          handleSaveRef.current?.();
          handleSaveLabelRef.current?.();
        }
        isDirtyRef.current = false;
        isNavigatingRef.current = true;
        setIsDirty(false);
        setTimeout(() => { window.location.href = href; }, result.isConfirmed ? 500 : 0);
      });
    };
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);

  const getGroupData = () => {
    const groupId = user?.groupId?._id;
    if (!groupId) return;
    ApiClient.get('api/group/detail', { id: groupId }).then(res => {
      if (res.success && res.data) {
        setConnectMeetLabel(res.data.connectMeetLabel || 'ConnectMeet');
      }
    });
  };

  const handleSaveLabel = () => {
    const groupId = user?.groupId?._id;
    if (!groupId) return;
    loader(true);
    ApiClient.put('api/group', { id: groupId, connectMeetLabel, testimonialLabel, hubThreshold, superhubThreshold }).then(res => {
      if (res.success) {
        const updatedUser = {
          ...user,
          connectMeetLabel,
          testimonialLabel,
          hubThreshold,
          superhubThreshold
        };
        dispatch(login_success(updatedUser));
      }
      loader(false);
    });
  };

  const handleBgUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBgUploading(true);
    const res = await ApiClient.azureUpload({ file });
    if (res.fileName) {
      setBackgroundImageUrl(res.fileName);
    } else {
      toast.error('Failed to upload background image');
    }
    setBgUploading(false);
  };

  const handleBgRemove = () => {
    if (backgroundImageUrl) {
      ApiClient.azureBlobDelete({ fileName: backgroundImageUrl }).then(() => {
        setBackgroundImageUrl('');
        if (bgInputRef.current) bgInputRef.current.value = '';
      });
    }
  };

  const getBgFullUrl = () => {
    if (!backgroundImageUrl) return '';
    return `${environment.sasurl}/${environment.container}/${encodeURIComponent(backgroundImageUrl)}`;
  };

  const handleSave = () => {
    const logoPayload = {
      companyLogoUrl,
      connectmeetLogoX: logoSettings.connectmeet.x,
      connectmeetLogoY: logoSettings.connectmeet.y,
      connectmeetLogoSize: logoSettings.connectmeet.size,
      connectmeetLogoPadding: logoSettings.connectmeet.padding,
      reminderLogoX: logoSettings.reminder.x,
      reminderLogoY: logoSettings.reminder.y,
      reminderLogoSize: logoSettings.reminder.size,
      reminderLogoPadding: logoSettings.reminder.padding,
      invitationLogoX: logoSettings.invitation.x,
      invitationLogoY: logoSettings.invitation.y,
      invitationLogoSize: logoSettings.invitation.size,
      invitationLogoPadding: logoSettings.invitation.padding,
      rsvpLogoX: logoSettings.rsvp.x,
      rsvpLogoY: logoSettings.rsvp.y,
      rsvpLogoSize: logoSettings.rsvp.size,
      rsvpLogoPadding: logoSettings.rsvp.padding,
      backgroundImageUrl,
      backgroundImageSize,
      textBlockX,
      textBlockY,
      textBlockWidth,
    };

    const cleanQuill = (v) => hasQuillContent(v) ? v : '';
    const parseReminderDays = (txt) => {
      const arr = (txt || '').split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n) && n > 0);
      return arr.length ? arr : [7, 1];
    };

    if (getdata && getdata.length > 0) {
      const id = getdata[0]?._id
      let payload = {
        id: id,
        groupMemberLimit: memberGroup,
        historyMonths: form,
        oddNumberHandling: oddHandling,
        emailHeaderText: cleanQuill(emailHeaderText), emailFooterText: cleanQuill(emailFooterText),
        reminderEmailHeaderText: cleanQuill(reminderEmailHeaderText), reminderEmailFooterText: cleanQuill(reminderEmailFooterText),
        reminderDays: parseReminderDays(reminderDaysText),
        invitationEmailHeaderText: cleanQuill(invitationEmailHeaderText), invitationEmailFooterText: cleanQuill(invitationEmailFooterText),
        rsvpEmailHeaderText: cleanQuill(rsvpEmailHeaderText), rsvpEmailFooterText: cleanQuill(rsvpEmailFooterText), rsvpHideDateTime, rsvpHideLocation,
        allowGuestToGuestThankYou,
        ...logoPayload
      }
      ApiClient.put('api/event-group/setting/update', payload).then(res => {
        if (res.success) {
          setIsDirty(false);
          initialLoadDone.current = false;
          toast(res?.message)
          getSettingData()
        }
      });
    }
    else {
      const id = user?.groupId?._id;
      let payload = {
        groupId: id,
        groupMemberLimit: memberGroup,
        historyMonths: form,
        emailHeaderText: cleanQuill(emailHeaderText), emailFooterText: cleanQuill(emailFooterText),
        reminderEmailHeaderText: cleanQuill(reminderEmailHeaderText), reminderEmailFooterText: cleanQuill(reminderEmailFooterText),
        reminderDays: parseReminderDays(reminderDaysText),
        invitationEmailHeaderText: cleanQuill(invitationEmailHeaderText), invitationEmailFooterText: cleanQuill(invitationEmailFooterText),
        rsvpEmailHeaderText: cleanQuill(rsvpEmailHeaderText), rsvpEmailFooterText: cleanQuill(rsvpEmailFooterText), rsvpHideDateTime, rsvpHideLocation,
        allowGuestToGuestThankYou,
        ...logoPayload
      }
      ApiClient.post(`api/event-group/setting`, payload).then(res => {
        if (res) {
          setIsDirty(false);
          initialLoadDone.current = false;
          toast(res?.message)
          getSettingData()
        }
      });
    }
  };

  // Keep save refs current for the click interceptor
  handleSaveRef.current = handleSave;
  handleSaveLabelRef.current = handleSaveLabel;

  localStorage.setItem("connectMeetHistory", form)

  const handleChange = (event) => {
    const value = event?.target?.value;
    if (value === '' || (/^\d+$/.test(value) && parseInt(value) > 0)) {
      setMemberGroup(value);
    } else if (value !== '') {
      toast.error("Group size must be a positive number");
    }
  };

  // cw: Logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoUploading(true);
    const res = await ApiClient.azureUpload({ file });
    if (res.fileName) {
      setCompanyLogoUrl(res.fileName);
    } else {
      toast.error('Failed to upload logo');
    }
    setLogoUploading(false);
  };

  const handleLogoRemove = () => {
    if (companyLogoUrl) {
      ApiClient.azureBlobDelete({ fileName: companyLogoUrl }).then(() => {
        setCompanyLogoUrl('');
        if (logoInputRef.current) logoInputRef.current.value = '';
      });
    }
  };

  const getLogoFullUrl = () => {
    if (!companyLogoUrl) return '';
    return `${environment.sasurl}/${environment.container}/${encodeURIComponent(companyLogoUrl)}`;
  };

  // cw: Update logo settings for current email type
  const updateLogoSetting = (key, value) => {
    setLogoSettings(prev => ({
      ...prev,
      [emailEditMode]: { ...prev[emailEditMode], [key]: value }
    }));
  };

  // cw: Apply current email's logo settings to all other emails
  const applyLogoToAll = () => {
    const current = logoSettings[emailEditMode];
    setLogoSettings({
      connectmeet: { ...current },
      reminder: { ...current },
      invitation: { ...current },
      rsvp: { ...current },
    });
    toast.success('Logo settings applied to all emails');
  };

  // cw: Wrapper to render DraggableLogo with current settings
  const renderLogo = (emailType) => (
    <DraggableLogo
      logoUrl={getLogoFullUrl()}
      settings={logoSettings[emailType]}
      onUpdateSetting={(key, value) => {
        setLogoSettings(prev => ({
          ...prev,
          [emailType]: { ...prev[emailType], [key]: value }
        }));
      }}
    />
  );

  // cw: Wrap preview content with background image - auto-fits text block inside template
  const renderPreviewContent = (emailType, innerContent, innerStyle = {}) => {
    const bgUrl = getBgFullUrl();
    if (bgUrl) {
      return (
        <div style={{ position: 'relative' }}>
          <img
            src={bgUrl}
            alt="Background Template"
            style={{ width: '100%', height: 'auto', display: 'block' }}
            draggable={false}
          />
          <div style={{
            position: 'absolute', top: '5%', left: '5%', width: '90%', height: '90%',
            overflow: 'auto', display: 'flex', flexDirection: 'column',
          }}>
            {renderLogo(emailType)}
            <div style={{ background: 'rgba(255,255,255,0.9)', borderRadius: '6px', padding: '15px', flex: 1, overflow: 'auto', ...innerStyle }}>
              {innerContent}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div style={{ position: 'relative', background: '#fff', ...innerStyle }}>
        {renderLogo(emailType)}
        {innerContent}
      </div>
    );
  };


  // cw: Logo controls (size slider + apply-to-all button) shown under each preview
  const LogoControls = () => {
    const settings = logoSettings[emailEditMode];
    return (
      <div className='mt-3 p-3 bg-gray-50 rounded-lg'>
        <div className='flex items-center gap-4 flex-wrap'>
          <div className='flex items-center gap-2 flex-1 min-w-[200px]'>
            <label className='text-sm text-gray-600 whitespace-nowrap'>Size:</label>
            <input
              type="range"
              min="40"
              max="400"
              value={settings.size}
              onChange={(e) => updateLogoSetting('size', parseInt(e.target.value))}
              className='flex-1'
            />
            <span className='text-sm text-gray-500 w-12'>{settings.size}px</span>
          </div>
          <div className='flex items-center gap-2 flex-1 min-w-[200px]'>
            <label className='text-sm text-gray-600 whitespace-nowrap'>Space below:</label>
            <input
              type="range"
              min="0"
              max="100"
              value={settings.padding}
              onChange={(e) => updateLogoSetting('padding', parseInt(e.target.value))}
              className='flex-1'
            />
            <span className='text-sm text-gray-500 w-12'>{settings.padding}px</span>
          </div>
          <button
            type="button"
            className='bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1.5 rounded-md text-sm font-medium transition-all'
            onClick={applyLogoToAll}
          >
            Apply logo to all emails
          </button>
        </div>
        <p className='text-xs text-gray-400 mt-1'>Drag the logo in the preview to reposition it</p>
      </div>
    );
  };

  if (isLoading || form === null) {
    return (
      <Layout>
        <div className='mb-6'>
          <h3 className="text-2xl font-semibold text-[#111827]"> Settings</h3>
          <p className="text-sm font-normal text-[#75757A]">Here you can see all about your Settings</p>
        </div>
        <div className="text-center py-8">
          <img src="/assets/img/loader.gif" className="pageLoader" alt="Loading..." />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className='mb-6'>
        <h3 className="text-2xl font-semibold text-[#111827]"> Settings</h3>
        <p className="text-sm font-normal text-[#75757A]">Here you can see all about your Settings</p>
      </div>

      <div className='shadow-box bg-white rounded-lg mt-6 p-2 lg:p-6 w-full '>
        <div className='grid grid-cols-12 gap-4'>
          <div className='col-span-12 md:col-span-6'>
            <div className='inputs_date'>
              <label className='mb-1 block'>Group size</label>
              <input
                type="text"
                placeholder="Member Group"
                className='relative shadow-box bg-white w-full rounded-lg h-10 flex items-center gap-2 overflow-hidden px-2 border border-gray-100'
                value={memberGroup}
                onChange={handleChange}
                onKeyPress={(e) => {
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </div>
          </div>

          <div className='col-span-12 md:col-span-6'>
            <FormControl
              type="select"
              label={`${connectMeetLabel} History`}
              displayValue="name"
              placeholder="Select ConnectMeet History"
              value={form}
              onChange={e => setForm(e)}
              options={options}
              theme="search"
            />
          </div>
        </div>

        <div className='col-span-12 md:col-span-6'>
          <FormControl
            type="select"
            label="If number of participants is not divisible by group settings:"
            displayValue="name"
            placeholder="Select group balancing method"
            value={oddHandling}
            onChange={e => setOddHandling(e)}
            options={oddHandlingOptions}
            theme="search"
          />
        </div>

        {/* cw: ConnectMeet Label Setting */}
        <div className='col-span-12 md:col-span-6 mt-4'>
          <label className='mb-1 block'>ConnectMeet Label</label>
          <p className='text-sm text-gray-500 mb-2'>
            Customize the name for "ConnectMeet" across your group
          </p>
          <input
            type="text"
            placeholder="ConnectMeet"
            className='relative shadow-box bg-white w-full rounded-lg h-10 flex items-center gap-2 overflow-hidden px-2 border border-gray-100'
            value={connectMeetLabel}
            onChange={(e) => setConnectMeetLabel(e.target.value)}
          />
        </div>

        {/* cw: Thank You Label Setting */}
        <div className='col-span-12 md:col-span-6 mt-4'>
          <label className='mb-1 block'>Thank You Label</label>
          <p className='text-sm text-gray-500 mb-2'>
            Customize the name for "Thank Yous" across your group (e.g., "Thanks", "Referrals", "Kudos")
          </p>
          <input
            type="text"
            placeholder="Thank Yous"
            className='relative shadow-box bg-white w-full rounded-lg h-10 flex items-center gap-2 overflow-hidden px-2 border border-gray-100'
            value={testimonialLabel}
            onChange={(e) => setTestimonialLabel(e.target.value)}
          />
        </div>

        {/* cw: Guest-to-Guest Thank You Toggle */}
        <div className='col-span-12 md:col-span-6 mt-4'>
          <label className='mb-1 block'>Guest-to-Guest {testimonialLabel}</label>
          <p className='text-sm text-gray-500 mb-2'>
            Allow guests to give {testimonialLabel.toLowerCase()} to other guests
          </p>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={allowGuestToGuestThankYou}
              onChange={(e) => setAllowGuestToGuestThankYou(e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            <span className="ml-3 text-sm font-medium text-gray-700">{allowGuestToGuestThankYou ? 'Enabled' : 'Disabled'}</span>
          </label>
        </div>

        {/* cw: Hub/Superhub Thresholds */}
        <div className='col-span-12 mt-4'>
          <h4 className='text-lg font-semibold text-[#111827] mb-2'>{testimonialLabel} Network Settings</h4>
          <p className='text-sm text-gray-500 mb-4'>
            Configure the thresholds for hub and superhub status in the {testimonialLabel.toLowerCase()} network
          </p>
        </div>

        <div className='col-span-12 md:col-span-6'>
          <label className='mb-1 block'>Hub Threshold</label>
          <p className='text-sm text-gray-500 mb-2'>
            Number of {testimonialLabel.toLowerCase()} (given + received) to become a Hub (green)
          </p>
          <input
            type="number"
            min="1"
            placeholder="5"
            className='relative shadow-box bg-white w-full rounded-lg h-10 flex items-center gap-2 overflow-hidden px-2 border border-gray-100'
            value={hubThreshold}
            onChange={(e) => setHubThreshold(parseInt(e.target.value) || 5)}
          />
        </div>

        <div className='col-span-12 md:col-span-6'>
          <label className='mb-1 block'>Superhub Threshold</label>
          <p className='text-sm text-gray-500 mb-2'>
            Number of {testimonialLabel.toLowerCase()} (given + received) to become a Superhub (gold)
          </p>
          <input
            type="number"
            min="1"
            placeholder="10"
            className='relative shadow-box bg-white w-full rounded-lg h-10 flex items-center gap-2 overflow-hidden px-2 border border-gray-100'
            value={superhubThreshold}
            onChange={(e) => setSuperhubThreshold(parseInt(e.target.value) || 10)}
          />
        </div>

        {/* cw: Email Header/Footer Settings */}
        <div className='mt-6 pt-6 border-t border-gray-200'>
          <h4 className='text-lg font-semibold text-[#111827] mb-2'>Email Customization</h4>
          <p className='text-sm text-gray-500 mb-4'>
            Customize the header and footer text for emails sent to your group members
          </p>

          {/* cw: Company Logo Upload */}
          <div className='mb-6 p-4 bg-gray-50 rounded-lg'>
            <label className='mb-2 block font-medium'>Company Logo</label>
            <p className='text-sm text-gray-500 mb-3'>
              Upload your company logo to display in emails. You can drag it to position and resize it per email type.
            </p>
            <div className='flex items-center gap-4'>
              <label className='inline-block text-white bg-orange-500 hover:bg-orange-600 font-medium rounded-lg text-sm px-5 py-2.5 text-center cursor-pointer'>
                <input
                  type="file"
                  className='hidden'
                  ref={logoInputRef}
                  accept="image/*"
                  onChange={handleLogoUpload}
                  disabled={logoUploading}
                />
                {logoUploading ? 'Uploading...' : companyLogoUrl ? 'Change Logo' : 'Upload Logo'}
              </label>
              {companyLogoUrl && (
                <>
                  <img
                    src={getLogoFullUrl()}
                    alt="Company Logo"
                    style={{ height: '40px', width: 'auto', borderRadius: '4px', border: '1px solid #e5e7eb' }}
                  />
                  <button
                    type="button"
                    className='text-red-500 hover:text-red-700 text-sm font-medium'
                    onClick={handleLogoRemove}
                  >
                    Remove
                  </button>
                </>
              )}
            </div>
          </div>

          {/* cw: Background Template Image Upload */}
          <div className='mb-6 p-4 bg-gray-50 rounded-lg'>
            <label className='mb-2 block font-medium'>Background Template Image</label>
            <p className='text-sm text-gray-500 mb-3'>
              Upload a background template image. Email content will be displayed on top of it. Drag the text block in the preview to position it.
            </p>
            <div className='flex items-center gap-4'>
              <label className='inline-block text-white bg-orange-500 hover:bg-orange-600 font-medium rounded-lg text-sm px-5 py-2.5 text-center cursor-pointer'>
                <input
                  type="file"
                  className='hidden'
                  ref={bgInputRef}
                  accept="image/*"
                  onChange={handleBgUpload}
                  disabled={bgUploading}
                />
                {bgUploading ? 'Uploading...' : backgroundImageUrl ? 'Change Background' : 'Upload Background'}
              </label>
              {backgroundImageUrl && (
                <>
                  <img
                    src={getBgFullUrl()}
                    alt="Background"
                    style={{ height: '40px', width: 'auto', borderRadius: '4px', border: '1px solid #e5e7eb' }}
                  />
                  <button
                    type="button"
                    className='text-red-500 hover:text-red-700 text-sm font-medium'
                    onClick={handleBgRemove}
                  >
                    Remove
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Toggle between email types - Chrome folder tab style */}
          <div className='flex mb-6 items-end border-b border-gray-200'>
            {[
              { key: 'connectmeet', label: `${connectMeetLabel} Email` },
              { key: 'reminder', label: 'Reminder Email' },
              { key: 'invitation', label: 'Add Members Email' },
              { key: 'rsvp', label: 'Event RSVP Email' },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`py-2.5 px-5 text-sm font-medium transition-all relative ${
                  emailEditMode === tab.key
                    ? 'bg-orange-500 text-white rounded-t-lg -mb-px z-10'
                    : 'text-gray-600 hover:text-gray-800 bg-gray-200 rounded-t-lg'
                }`}
                style={emailEditMode === tab.key ? {
                  borderTopLeftRadius: '10px',
                  borderTopRightRadius: '10px',
                  paddingBottom: '11px',
                } : {
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                }}
                onClick={() => setEmailEditMode(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* ConnectMeet Group Email Customization */}
          {emailEditMode === 'connectmeet' && (
            <>
              <div className='grid grid-cols-12 gap-4'>
                <div className='col-span-12'>
                  <label className='block font-medium mb-1'>Email Header Text</label>
                  <p className='text-sm text-gray-500 mb-2'>This text will appear at the top of the email, before the group assignments</p>
                  <div className="quills"><ReactQuill theme="snow" formats={quillFormats} modules={quillModules} placeholder="e.g., Welcome to this week's networking session!" value={emailHeaderText || ''} onChange={setEmailHeaderText} /></div>
                </div>
                <div className='col-span-12'>
                  <label className='block font-medium mb-1'>Email Footer Text</label>
                  <p className='text-sm text-gray-500 mb-2'>This text will appear at the bottom of the email, after the group assignments</p>
                  <div className="quills"><ReactQuill theme="snow" formats={quillFormats} modules={quillModules} placeholder="e.g., If you have any questions, please contact your group leader." value={emailFooterText || ''} onChange={setEmailFooterText} /></div>
                </div>
              </div>

              {/* ConnectMeet Email Preview */}
              <div className='mt-6'>
                <label className='mb-2 block font-medium text-gray-700'>Preview</label>
                <div style={{ fontFamily: "'Poppins', sans-serif", background: '#f3f3f3', padding: '30px', borderRadius: '8px' }}>
                  <div style={{ maxWidth: '676px', margin: '0 auto', padding: '0' }}>
                    {renderPreviewContent('connectmeet', <>
                      <div style={{ padding: '0 0 20px 0' }}>
                        <p style={{ fontSize: '20px', width: '80%', margin: '0 auto', fontWeight: 'bold', color: '#393C3D', lineHeight: '24px', display: 'flex', alignItems: 'center' }}>
                          <span style={{ marginTop: '5px', marginRight: '5px', color: '#f2783c' }}>📍</span>
                          Here are the groups for <span style={{ marginLeft: '4px' }}>{'{'}<span>Event Name</span>{'}'}</span>
                        </p>
                      </div>
                      {hasQuillContent(emailHeaderText) && (
                        <div style={{ padding: '10px 0 20px 0' }}>
                          <div className="ql-editor" style={{ fontSize: '14px', width: '80%', margin: '0 auto', color: '#393C3D', lineHeight: '22px', padding: 0, minHeight: 'auto' }} dangerouslySetInnerHTML={{ __html: emailHeaderText }} />
                        </div>
                      )}
                      <div style={{ width: '80%', margin: '10px auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead><tr><th colSpan={2} style={{ padding: '10px 20px', textAlign: 'left', fontSize: '16px', borderTop: '1px solid #cdcdcd', borderLeft: '1px solid #cdcdcd', borderRight: '1px solid #cdcdcd', color: '#272727', fontWeight: '600' }}>Group 1</th></tr></thead>
                          <tbody>
                            <tr><td style={{ padding: '10px 20px', textAlign: 'left', fontSize: '12px', border: '1px solid #cdcdcd', color: '#272727', fontWeight: '600' }}>{'{'}<span>User1 Name</span>{'}'}</td><td style={{ padding: '10px 20px', textAlign: 'left', fontSize: '12px', border: '1px solid #cdcdcd', color: '#272727', fontWeight: '600' }}>{'{'}<span>user1@email.com</span>{'}'}</td></tr>
                            <tr><td style={{ padding: '10px 20px', textAlign: 'left', fontSize: '12px', border: '1px solid #cdcdcd', color: '#272727', fontWeight: '600' }}>{'{'}<span>User2 Name</span>{'}'}</td><td style={{ padding: '10px 20px', textAlign: 'left', fontSize: '12px', border: '1px solid #cdcdcd', color: '#272727', fontWeight: '600' }}>{'{'}<span>user2@email.com</span>{'}'}</td></tr>
                            <tr><td style={{ padding: '10px 20px', textAlign: 'left', fontSize: '12px', border: '1px solid #cdcdcd', color: '#272727', fontWeight: '600' }}>{'{'}<span>User3 Name</span>{'}'}</td><td style={{ padding: '10px 20px', textAlign: 'left', fontSize: '12px', border: '1px solid #cdcdcd', color: '#272727', fontWeight: '600' }}>{'{'}<span>user3@email.com</span>{'}'}</td></tr>
                          </tbody>
                        </table>
                      </div>
                      {hasQuillContent(emailFooterText) && (
                        <div style={{ padding: '20px 0 10px 0' }}>
                          <div className="ql-editor" style={{ fontSize: '14px', width: '80%', margin: '0 auto', color: '#393C3D', lineHeight: '22px', padding: 0, minHeight: 'auto' }} dangerouslySetInnerHTML={{ __html: emailFooterText }} />
                        </div>
                      )}
                      <div style={{ height: '40px' }}></div>
                      <div style={{ width: '80%', margin: '0 auto', paddingBottom: '30px' }}>
                        <p style={{ fontSize: '15px', fontWeight: 'bold', color: '#393C3D', lineHeight: '24px' }}>{'{'}<span>{connectMeetLabel} Manager Name</span>{'}'} {'{'}<span>manager@email.com</span>{'}'}</p>
                      </div>
                    </>)}
                  </div>
                </div>
                {companyLogoUrl && <LogoControls />}
              </div>
            </>
          )}

          {/* Reminder Email Customization */}
          {emailEditMode === 'reminder' && (
            <>
              <div className='grid grid-cols-12 gap-4'>
                <div className='col-span-12'>
                  <label className='block font-medium mb-1'>Reminder Email Schedule (days before event)</label>
                  <p className='text-sm text-gray-500 mb-2'>Enter one or more numbers separated by commas — each one sends a separate reminder email that many days before the event. For example, <span className='font-medium'>7, 1</span> sends one reminder 7 days before and another 1 day before. Applies to every event in this group unless you override it on an individual event. (Default: 7, 1)</p>
                  <input
                    type="text"
                    value={reminderDaysText}
                    onChange={(e) => setReminderDaysText(e.target.value)}
                    placeholder="7, 1"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <div className='col-span-12'>
                  <label className='block font-medium mb-1'>Reminder Email Header Text</label>
                  <p className='text-sm text-gray-500 mb-2'>This text will appear at the top of the reminder email, before the greeting</p>
                  <div className="quills"><ReactQuill theme="snow" formats={quillFormats} modules={quillModules} placeholder="e.g., Don't forget about our upcoming event!" value={reminderEmailHeaderText || ''} onChange={setReminderEmailHeaderText} /></div>
                </div>
                <div className='col-span-12'>
                  <label className='block font-medium mb-1'>Reminder Email Footer Text</label>
                  <p className='text-sm text-gray-500 mb-2'>This text will appear at the bottom of the reminder email, after the event details</p>
                  <div className="quills"><ReactQuill theme="snow" formats={quillFormats} modules={quillModules} placeholder="e.g., See you there! — Your Group Leader" value={reminderEmailFooterText || ''} onChange={setReminderEmailFooterText} /></div>
                </div>
              </div>

              {/* Reminder Email Preview */}
              <div className='mt-6'>
                <label className='mb-2 block font-medium text-gray-700'>Preview</label>
                <div style={{ fontFamily: "'Poppins', sans-serif", background: '#f3f3f3', padding: '30px', borderRadius: '8px' }}>
                  <div style={{ maxWidth: '676px', margin: '0 auto', color: '#393C3D', fontSize: '14px', lineHeight: '1.5' }}>
                    {renderPreviewContent('reminder', <>
                      <div style={{ padding: '0' }}>
                        <p style={{ fontSize: '16px', width: '80%', margin: '0 auto', fontWeight: 'bold', color: '#393C3D', lineHeight: '24px', marginBottom: '0px' }}>
                          Hi <span style={{ marginLeft: '5px' }}>{'{'}<span>User1 Name</span>{'}'}</span>
                        </p>
                      </div>
                      {hasQuillContent(reminderEmailHeaderText) && (
                        <div style={{ padding: '10px 0 10px 0' }}>
                          <div className="ql-editor" style={{ fontSize: '16px', width: '80%', margin: '0 auto', color: '#6D6D6D', lineHeight: '25px', padding: 0, minHeight: 'auto' }} dangerouslySetInnerHTML={{ __html: reminderEmailHeaderText }} />
                        </div>
                      )}
                      <div style={{ padding: '5px 0 10px 0' }}>
                        <p style={{ fontSize: '16px', width: '80%', margin: '0 auto', color: '#6D6D6D', lineHeight: '25px' }}>
                          This is a friendly reminder that <span style={{ fontWeight: '600', textTransform: 'capitalize' }}>{'{'}<span>Event Name</span>{'}'}</span> is happening in <span style={{ fontWeight: '600' }}>{'{'}<span>Number of Days</span>{'}'}</span> days! It will be held on <span style={{ fontWeight: '600' }}>{'{'}<span>Event Date</span>{'}'}</span> at <span style={{ fontWeight: '600' }}>{'{'}<span>Event Location</span>{'}'}</span>, and we would love for you to join us.
                        </p>
                      </div>
                      <div style={{ padding: '5px 0 25px 0' }}>
                        <p style={{ fontSize: '16px', width: '80%', margin: '0 auto', color: '#6D6D6D', lineHeight: '25px' }}>We are looking forward to seeing you there.</p>
                      </div>
                      {hasQuillContent(reminderEmailFooterText) && (
                        <div style={{ padding: '0 0 20px 0' }}>
                          <div className="ql-editor" style={{ fontSize: '16px', width: '80%', margin: '0 auto', color: '#6D6D6D', lineHeight: '25px', padding: 0, minHeight: 'auto' }} dangerouslySetInnerHTML={{ __html: reminderEmailFooterText }} />
                        </div>
                      )}
                      <div style={{ textAlign: 'center', padding: '10px 0 30px 0' }}>
                        <span style={{ background: '#EF7A2B', color: '#fff', padding: '12px 10px', display: 'inline-block', width: '220px', fontSize: '14px', textAlign: 'center', textDecoration: 'none' }}>Click here to check your registration status</span>
                      </div>
                    </>)}
                  </div>
                </div>
                {companyLogoUrl && <LogoControls />}
              </div>
            </>
          )}

          {/* Add Members Invitation Email Customization */}
          {emailEditMode === 'invitation' && (
            <>
              <div className='grid grid-cols-12 gap-4'>
                <div className='col-span-12'>
                  <label className='block font-medium mb-1'>Invitation Email Header Text</label>
                  <p className='text-sm text-gray-500 mb-2'>This text will appear at the top of the invitation email, before the invitation message</p>
                  <div className="quills"><ReactQuill theme="snow" formats={quillFormats} modules={quillModules} placeholder="e.g., Welcome! We're excited to have you join our group." value={invitationEmailHeaderText || ''} onChange={setInvitationEmailHeaderText} /></div>
                </div>
                <div className='col-span-12'>
                  <label className='block font-medium mb-1'>Invitation Email Footer Text</label>
                  <p className='text-sm text-gray-500 mb-2'>This text will appear at the bottom of the invitation email, after the invitation message</p>
                  <div className="quills"><ReactQuill theme="snow" formats={quillFormats} modules={quillModules} placeholder="e.g., If you have any questions, feel free to reach out to your group leader." value={invitationEmailFooterText || ''} onChange={setInvitationEmailFooterText} /></div>
                </div>
              </div>

              {/* Invitation Email Preview */}
              <div className='mt-6'>
                <label className='mb-2 block font-medium text-gray-700'>Preview</label>
                <div style={{ fontFamily: "'Poppins', sans-serif", background: '#f3f3f3', padding: '30px', borderRadius: '8px' }}>
                  <div style={{ maxWidth: '676px', margin: '0 auto', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    {renderPreviewContent('invitation', <>
                      <div style={{ textAlign: 'center', padding: '40px 20px 20px' }}>
                        <p style={{ fontSize: '18px', fontWeight: '600', color: '#393C3D', margin: '0' }}>Hi {'{'}<span>User1 Name</span>{'}'},</p>
                      </div>
                      {hasQuillContent(invitationEmailHeaderText) && (
                        <div style={{ textAlign: 'center', padding: '10px 40px 0' }}>
                          <div className="ql-editor" style={{ fontSize: '16px', color: '#393C3D', lineHeight: '26px', padding: 0, minHeight: 'auto' }} dangerouslySetInnerHTML={{ __html: invitationEmailHeaderText }} />
                        </div>
                      )}
                      <div style={{ textAlign: 'center', padding: '0 40px' }}>
                        <p style={{ fontSize: '16px', color: '#393C3D', lineHeight: '26px', margin: '0' }}>
                          <strong>{groupLeaderName}</strong> has invited you to join the networking group <strong style={{ color: '#EF7A2B' }}>{groupName}</strong> on SkillsNetPro.
                        </p>
                      </div>
                      {hasQuillContent(invitationEmailFooterText) && (
                        <div style={{ textAlign: 'center', padding: '10px 40px 10px' }}>
                          <div className="ql-editor" style={{ fontSize: '16px', color: '#393C3D', lineHeight: '26px', padding: 0, minHeight: 'auto' }} dangerouslySetInnerHTML={{ __html: invitationEmailFooterText }} />
                        </div>
                      )}
                      <div style={{ textAlign: 'center', padding: '30px 20px' }}>
                        <span style={{ background: '#EF7A2B', display: 'inline-block', color: '#fff', padding: '14px 40px', fontSize: '16px', fontWeight: '500', borderRadius: '6px' }}>Accept Invitation</span>
                      </div>
                      <div style={{ textAlign: 'center', padding: '0 40px 30px' }}>
                        <p style={{ fontSize: '13px', color: '#888', margin: '0' }}>This invitation link will expire in 7 days and can only be used once.</p>
                      </div>
                    </>)}
                  </div>
                </div>
                {companyLogoUrl && <LogoControls />}
              </div>
            </>
          )}

          {/* Event RSVP Email Customization */}
          {emailEditMode === 'rsvp' && (
            <>
              <div className='grid grid-cols-12 gap-4'>
                <div className='col-span-12'>
                  <label className='block font-medium mb-1'>RSVP Email Header Text</label>
                  <p className='text-sm text-gray-500 mb-2'>This text will appear at the top of the RSVP email, after the greeting</p>
                  <div className="quills"><ReactQuill theme="snow" formats={quillFormats} modules={quillModules} placeholder="e.g., We have an exciting event coming up!" value={rsvpEmailHeaderText || ''} onChange={setRsvpEmailHeaderText} /></div>
                </div>
                <div className='col-span-12'>
                  <label className='block font-medium mb-1'>RSVP Email Footer Text</label>
                  <p className='text-sm text-gray-500 mb-2'>This text will appear at the bottom of the RSVP email, after the RSVP buttons</p>
                  <div className="quills"><ReactQuill theme="snow" formats={quillFormats} modules={quillModules} placeholder="e.g., Looking forward to seeing you there!" value={rsvpEmailFooterText || ''} onChange={setRsvpEmailFooterText} /></div>
                </div>
                <div className='col-span-12'>
                  <label className='block font-medium mb-2'>Exclude from RSVP Email</label>
                  <div className='flex items-center gap-6'>
                    <label className='flex items-center gap-2 cursor-pointer'>
                      <input type='checkbox' checked={rsvpHideDateTime} onChange={(e) => setRsvpHideDateTime(e.target.checked)} className='w-4 h-4 accent-orange-500' />
                      <span className='text-sm text-gray-700'>Date & Time</span>
                    </label>
                    <label className='flex items-center gap-2 cursor-pointer'>
                      <input type='checkbox' checked={rsvpHideLocation} onChange={(e) => setRsvpHideLocation(e.target.checked)} className='w-4 h-4 accent-orange-500' />
                      <span className='text-sm text-gray-700'>Location</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* RSVP Email Preview */}
              <div className='mt-6'>
                <label className='mb-2 block font-medium text-gray-700'>Preview</label>
                <div style={{ fontFamily: "'Poppins', sans-serif", background: '#f3f3f3', padding: '30px', borderRadius: '8px' }}>
                  <div style={{ maxWidth: '676px', margin: '0 auto' }}>
                    {renderPreviewContent('rsvp', <>
                      <div style={{ padding: '20px 60px 10px' }}>
                        <div style={{ borderBottom: '1px solid #d77c2f' }}></div>
                      </div>
                      <div style={{ padding: '10px 60px' }}>
                        <p style={{ fontSize: '20px', fontWeight: '500', padding: '5px 0px 15px', color: '#000', lineHeight: '18px' }}>
                          Hi <span style={{ fontWeight: '500', color: '#EF7A2B' }}>{'{'}<span>User1 Name</span>{'}'}</span>,
                        </p>
                      </div>
                      {hasQuillContent(rsvpEmailHeaderText) && (
                        <div style={{ padding: '7px 60px 10px', color: '#393C3D', lineHeight: '22px', fontSize: '14px' }}><div className="ql-editor" style={{ padding: 0, minHeight: 'auto' }} dangerouslySetInnerHTML={{ __html: rsvpEmailHeaderText }} /></div>
                      )}
                      {!rsvpHideDateTime && (
                      <div style={{ padding: '7px 60px 5px', color: '#000', fontSize: '13px', textTransform: 'capitalize' }}>
                        <span style={{ marginRight: '5px', color: '#EF7A2B' }}>📅</span> {'{'}<span>Event Date</span>{'}'}
                      </div>
                      )}
                      {!rsvpHideLocation && (
                      <div style={{ padding: '7px 60px 20px', color: '#000', fontSize: '13px', textTransform: 'capitalize' }}>
                        <span style={{ marginRight: '5px', color: '#EF7A2B' }}>📍</span> {'{'}<span>Location</span>{'}'}
                      </div>
                      )}
                      <div style={{ padding: '7px 60px 15px', color: '#000', fontSize: '14px', fontWeight: '500', textTransform: 'capitalize' }}>Here are the details for your event:</div>
                      <div style={{ padding: '0px 60px' }}>
                        <p style={{ margin: '0px', border: '1px solid rgba(215, 124, 47, 0.58)', padding: '6px 9px', borderRadius: '4px' }}>
                          <span style={{ fontWeight: 'bold', fontSize: '15px', display: 'block', color: '#000' }}>Please RSVP</span>
                          <span style={{ fontWeight: 'bold', display: 'block' }}>{'{'}<span>Event Date</span>{'}'}</span>
                          <br />
                          <span style={{ display: 'block' }}>{'{'}<span>Event Description</span>{'}'}</span>
                        </p>
                      </div>
                      <div style={{ padding: '15px 60px', display: 'flex', gap: '10px' }}>
                        <span style={{ background: '#EF7A2B', color: '#fff', padding: '6px 12px', width: '80px', fontSize: '14px', textAlign: 'center', border: '1px solid #d77c2f', borderRadius: '3px', display: 'inline-block' }}>Yes</span>
                        <span style={{ border: '1px solid #EF7A2B', color: '#EF7A2B', padding: '6px 12px', width: '80px', fontSize: '14px', textAlign: 'center', borderRadius: '3px', display: 'inline-block', marginLeft: '10px' }}>No</span>
                      </div>
                      {hasQuillContent(rsvpEmailFooterText) && (
                        <div style={{ padding: '7px 60px 15px', color: '#393C3D', lineHeight: '22px', fontSize: '14px', textAlign: 'center' }}><div className="ql-editor" style={{ padding: 0, minHeight: 'auto' }} dangerouslySetInnerHTML={{ __html: rsvpEmailFooterText }} /></div>
                      )}
                      <div style={{ height: '30px' }}></div>
                    </>)}
                  </div>
                </div>
                {companyLogoUrl && <LogoControls />}
              </div>
            </>
          )}
        </div>

        <div className='flex items-center justify-end mt-3'>
          <button
            className='bg-orange-500 px-4 py-2 rounded-lg text-white mt-4'
            onClick={() => { handleSave(); handleSaveLabel(); }}
          >
            Save
          </button>
        </div>
      </div>

      {/* cw: Zoom Integration */}
      <div className='shadow-box bg-white rounded-lg mt-6 p-2 lg:p-6 w-full'>
        <h4 className='text-lg font-semibold text-[#111827] mb-2'>Zoom Integration</h4>
        <p className='text-sm text-gray-500 mb-4'>
          Connect your Zoom account to automatically pull meeting participant data for check-ins.
        </p>
        <div className='p-4 bg-gray-50 rounded-lg'>
          {zoomStatus.connected ? (
            <div className='flex items-center justify-between flex-wrap gap-4'>
              <div className='flex items-center gap-3'>
                <div className='w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center'>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 10.5V7C17 6.45 16.55 6 16 6H4C3.45 6 3 6.45 3 7V17C3 17.55 3.45 18 4 18H16C16.55 18 17 17.55 17 17V13.5L21 17.5V6.5L17 10.5Z" fill="#2D8CFF"/>
                  </svg>
                </div>
                <div>
                  <p className='text-sm font-medium text-gray-800'>Connected to Zoom</p>
                  {zoomStatus.zoomEmail && (
                    <p className='text-xs text-gray-500'>{zoomStatus.zoomEmail}</p>
                  )}
                </div>
              </div>
              <button
                className='bg-red-500 hover:bg-red-600 px-4 py-2 rounded-lg text-white text-sm font-medium transition-all'
                onClick={disconnectZoom}
              >
                Disconnect
              </button>
            </div>
          ) : (
            <div className='flex items-center justify-between flex-wrap gap-4'>
              <p className='text-sm text-gray-600'>No Zoom account connected</p>
              <button
                className='bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-white text-sm font-medium transition-all flex items-center gap-2'
                onClick={connectZoom}
                disabled={zoomLoading}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 10.5V7C17 6.45 16.55 6 16 6H4C3.45 6 3 6.45 3 7V17C3 17.55 3.45 18 4 18H16C16.55 18 17 17.55 17 17V13.5L21 17.5V6.5L17 10.5Z" fill="white"/>
                </svg>
                {zoomLoading ? 'Connecting...' : 'Connect Zoom'}
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Setting;
